/**
 * Key Handover Booking Query Schema
 *
 * Central definition of all query parameters persisted across the key handover flow.
 * All fields are optional to support partial state during progressive form completion.
 */

import { isRealCalendarDate } from "../../../../../util/date";
import { STATION_TIME_ZONE } from "../../../../../util/stationTime";
export interface BookingQuery {
  /** Location identifier (UUID or slug format: 6-64 alphanumeric chars with dashes) */
  locationId?: string;

  /** Service (asset group) identifier (UUID or slug format) */
  serviceId?: string;

  /** Pickup date in ISO format (YYYY-MM-DD) */
  pickupDate?: string;

  /** Pickup time in 24-hour format (HH:mm) */
  pickupTime?: string;

  /** Number of days for rental (numeric string) */
  pickupDays?: string;

  /** Return date in ISO format (YYYY-MM-DD) */
  returnDate?: string;

  /** Return time in 24-hour format (HH:mm) */
  returnTime?: string;

  /** Number of days for key return duration (numeric string) */
  returnDays?: string;

  /**
   * IANA timezone identifier (e.g., "Europe/Berlin"). Defaults to
   * `DEFAULT_TIMEZONE` if omitted. Parsed so an existing link keeps parsing,
   * but NOT read by schedule hydration: a schedule is always read on the
   * selected location's zone, and on the default zone when the URL names no
   * location or one that is not found (#3219, `useHydrationTimeZone`). The
   * server never reads a window in a zone the client names.
   */
  timezone?: string;

  /** Array of receiver IDs (non-sensitive identifiers). Max 10 receivers */
  receivers?: string[];

  /** Array of extra feature slugs (e.g., "cleaning", "insurance"). Max 15 extras */
  extras?: string[];
}

/**
 * Canonical ordering of keys for deterministic URL stringification.
 * Ensures consistent URLs across the application for better debugging and caching.
 */
export const BOOKING_QUERY_KEYS: readonly (keyof BookingQuery)[] = [
  "locationId",
  "serviceId",
  "pickupDate",
  "pickupTime",
  "pickupDays",
  "returnDate",
  "returnTime",
  "returnDays",
  "timezone",
  "receivers",
  "extras",
] as const;

/** Default timezone for booking queries when not explicitly specified. The
 *  schedule of a URL that names no location (or an unknown one) is read on
 *  the station default (`useHydrationTimeZone`), which this equals. */
export const DEFAULT_TIMEZONE = STATION_TIME_ZONE;

/** Maximum number of receiver IDs allowed in a single booking query */
export const MAX_RECEIVERS = 10;

/** Maximum number of extra features allowed in a single booking query */
export const MAX_EXTRAS = 15;

/**
 * Validation Patterns
 */

/** Lightweight ID pattern: alphanumeric and dashes, 6-64 chars (e.g., "loc-abc123", "uuid-a1b2c3d4") */
export const ID_PATTERN = /^[a-zA-Z0-9_-]{6,64}$/;

/** ISO date format (YYYY-MM-DD). Pattern only - use Date parsing for semantic validation */
export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** 24-hour time format (HH:mm). Pattern only - validate semantics separately */
export const TIME_PATTERN = /^\d{2}:\d{2}$/;

/** Extra feature slug: lowercase alphanumeric and dashes, 2-32 chars (e.g., "cleaning", "extra-insurance") */
export const EXTRA_PATTERN = /^[a-z0-9-]{2,32}$/;

/**
 * Type Guards for Runtime Validation
 */

/** Validate if a value is a valid ID string */
export function isValidId(value: unknown): value is string {
  return typeof value === "string" && ID_PATTERN.test(value);
}

/** Validate if a value is a valid ISO date string (format only, not semantic) */
export function isValidDate(value: unknown): value is string {
  return (
    typeof value === "string" &&
    DATE_PATTERN.test(value) &&
    // The shape is not enough: "2026-02-30" matches the pattern and is not a
    // day. Unchecked, it reached the flow and the first attempt to format it
    // threw `RangeError: Invalid time value`, which unmounted the whole app —
    // a white screen from a mistyped URL (#2764, E4).
    isRealCalendarDate(value)
  );
}

/** Validate if a value is a valid time string (format only, not semantic) */
export function isValidTime(value: unknown): value is string {
  return typeof value === "string" && TIME_PATTERN.test(value);
}

/** Validate if a value is a valid extra feature slug */
export function isValidExtra(value: unknown): value is string {
  return typeof value === "string" && EXTRA_PATTERN.test(value);
}

/** Validate if a value is a valid IANA timezone identifier using Intl API */
export function isValidTimezone(value: unknown): value is string {
  if (typeof value !== "string" || value.length < 3 || value.length > 64) {
    return false;
  }

  // Must contain a slash (except "UTC") and no whitespace
  const hasValidFormat = value.includes("/") || value === "UTC";
  if (!hasValidFormat || /\s/.test(value)) {
    return false;
  }

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}
